import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DOC_TYPES } from "@/lib/constants";
import { ArrowLeft, ArrowRight, Check, Loader2, X, Pencil } from "lucide-react";
import { WizardProgress } from "@/components/wizard/WizardUI";
import { ClientStep } from "@/components/wizard/ClientStep";
import { EquipmentStep, TextStep, ReportStep, ValueStep, DateStep, WarrantyStep, SignatureStep } from "@/components/wizard/Steps";
import { ReviewStep, FinishStep } from "@/components/wizard/ReviewFinish";
import { fmtBRL } from "@/components/CurrencyInput";
import PhotoUpload from "@/components/PhotoUpload";
import { AiDescribeButton } from "@/components/AiDescribe";

const today = () => new Date().toLocaleDateString("pt-BR");
const STEPS = {
  nota_servico: ["client", "equipment", "service", "warranty", "review", "sign", "finish"],
  service_order: ["client", "equipment", "service", "warranty", "review", "sign", "finish"],
  budget: ["client", "equipment", "defect", "warranty", "review", "finish"],
  report: ["client", "equipment", "defect", "report", "photos", "review", "sign", "finish"],
  recibo: ["client", "service", "notes", "review", "finish"],
  contrato: ["client", "service", "warranty", "review", "sign", "finish"],
  proposta_comercial: ["client", "service", "warranty", "review", "finish"],
};
const LABELS = { client: "Cliente", equipment: "Equipamento", service: "Serviço", defect: "Serviço", report: "Conclusão", photos: "Fotos", warranty: "Garantia", notes: "Observações", review: "Revisão", sign: "Assinatura", finish: "Pronto" };
const TITLES = { client: "Quem é o cliente?", equipment: "Qual é o equipamento?", service: "Descreva o serviço", defect: "Descreva o problema e o valor", report: "Conclusão técnica", photos: "Fotos e anexos", warranty: "Garantia e observações", notes: "Observações", review: "Revise antes de gerar", sign: "Assinatura", finish: "Documento gerado" };

const emptyDoc = (type, clientId) => ({
  type, client_id: clientId || "", date: today(), description: "", technician: "",
  equipment: { name: "", brand: "", model: "", serial: "", btu: "", gas: "", reported_defect: "" },
  diagnosis: "", inspection: "", probable_cause: "", procedure: "", recommendation: "", conclusion: "",
  items: [], labor: 0, discount: 0, payment_method: "", payment_status: type === "recibo" ? "PAGO" : "PENDENTE", payment_term: "", validity: "",
  warranty: { term: "", start: "", end: "", covered: "", not_covered: "" }, clauses: [], photos: [], observations: "", status: "Rascunho", segment_fields: {},
});

export default function DocumentWizard() {
  const { type: routeType, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [type, setType] = useState(routeType || "nota_servico");
  const steps = useMemo(() => (STEPS[type] || STEPS.nota_servico).map((s) => ({ id: s, label: LABELS[s] })), [type]);
  const draftKey = `nh-wizard-${id || `new-${type}`}`;

  const [doc, setDoc] = useState(() => emptyDoc(routeType || "nota_servico", location.state?.clientId));
  const [docId, setDocId] = useState(id || null);
  const [step, setStep] = useState(0);
  const [client, setClient] = useState(null);
  const [company, setCompany] = useState(null);
  const [equips, setEquips] = useState([]);
  const [sigs, setSigs] = useState({ client: null, tech: null });
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState("");

  const upd = useCallback((patch) => setDoc((d) => ({ ...d, ...patch })), []);
  const saveTemplates = async (list) => { const { data } = await api.post("/company", { ...company, service_templates: list }); setCompany(data); };
  const saveTemplate = async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    if ((company?.service_templates || []).includes(t)) { toast.error("Este modelo já existe"); return; }
    try { await saveTemplates([...(company?.service_templates || []), t]); toast.success("Modelo salvo!"); } catch (e) { toast.error("Não foi possível salvar o modelo"); }
  };
  const deleteTemplate = async (text) => { try { await saveTemplates((company?.service_templates || []).filter((x) => x !== text)); } catch (e) { toast.error("Erro ao remover"); } };
  const applyAi = (k, v) => setDoc((d) => {
    if (k === "problem") return { ...d, equipment: { ...d.equipment, reported_defect: v }, ...(type === "report" ? {} : {}) };
    if (k === "diagnosis") return { ...d, diagnosis: v };
    if (k === "recommended_service") return type === "report" ? { ...d, recommendation: v } : { ...d, description: v };
    if (k === "client_description") return type === "report" ? { ...d, description: v } : { ...d, observations: d.observations || v };
    if (k === "observations") return type === "report" ? { ...d, conclusion: v } : { ...d, observations: v };
    if (k === "warranty") return { ...d, warranty: { ...d.warranty, term: v } };
    return d;
  });

  useEffect(() => {
    api.get("/company").then(({ data }) => { setCompany(data); if (!id && data?.default_warranty?.term) setDoc((d) => ({ ...d, warranty: { ...d.warranty, ...data.default_warranty } })); }).catch(() => {});
    if (id) {
      api.get(`/documents/${id}`).then(({ data }) => { setType(data.type); setDoc({ ...emptyDoc(data.type), ...data, equipment: { ...emptyDoc(data.type).equipment, ...(data.equipment || {}) }, warranty: { ...emptyDoc(data.type).warranty, ...(data.warranty || {}) } }); }).catch(() => { toast.error("Documento não encontrado"); navigate("/documents"); });
    } else {
      try { const raw = localStorage.getItem(draftKey); if (raw) { const d = JSON.parse(raw); if (d?.doc?.client_id || d?.doc?.description || d?.doc?.equipment?.name || Number(d?.doc?.labor)) setDraft(d); } } catch (e) { console.error(e); }
    }
  }, [id]);

  useEffect(() => {
    if (saved) return;
    const t = setTimeout(() => localStorage.setItem(draftKey, JSON.stringify({ doc, step, docId, at: Date.now() })), 400);
    return () => clearTimeout(t);
  }, [doc, step, docId, draftKey, saved]);

  useEffect(() => {
    if (!doc.client_id) { setClient(null); setEquips([]); return; }
    api.get(`/clients/${doc.client_id}`).then(({ data }) => { setClient(data.client); }).catch(() => {});
    api.get("/equipments", { params: { client_id: doc.client_id } }).then(({ data }) => setEquips(data)).catch(() => setEquips([]));
  }, [doc.client_id]);

  useEffect(() => {
    const eq = location.state?.equipment;
    if (eq && !id) setDoc((d) => ({ ...d, equipment_id: eq.id, equipment: { ...d.equipment, name: eq.type === "Outro" ? eq.name : eq.type, brand: eq.brand || "", model: eq.model || "", serial: eq.serial || "", btu: eq.capacity || "" }, description: location.state?.description || d.description }));
  }, []);

  const cur = steps[step]?.id;
  const isService = ["nota_servico", "service_order"].includes(type);
  const validate = () => {
    if (cur === "client" && !doc.client_id) return "Selecione ou cadastre o cliente para continuar.";
    if (cur === "equipment" && !doc.equipment?.name?.trim() && type !== "budget") return "Informe o equipamento.";
    if (cur === "service" && !doc.description?.trim()) return "Descreva o serviço realizado.";
    if (cur === "defect" && !doc.equipment?.reported_defect?.trim()) return "Descreva o problema.";
    if (cur === "report" && !doc.diagnosis?.trim() && !doc.description?.trim()) return "Preencha ao menos a descrição técnica ou o diagnóstico.";
    return "";
  };

  const persist = async () => {
    const payload = { ...doc, type, items: (doc.items || []).filter((i) => i.description || Number(i.unit_price)).map(({ id: _i, ...r }) => r) };
    const res = docId ? await api.put(`/documents/${docId}`, payload) : await api.post("/documents", payload);
    if (!docId) setDocId(res.data.id);
    return res.data;
  };

  const next = async () => {
    const err = validate();
    if (err) { setError(err); toast.error(err); return; }
    setError("");
    if (cur === "review") {
      setSaving(true);
      try { await persist(); } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); setSaving(false); return; }
      setSaving(false);
    }
    if (cur === "sign") {
      setSaving(true);
      try {
        const d = await persist();
        let final = d;
        if (sigs.client || sigs.tech) {
          const r = await api.post(`/documents/${d.id}/signatures`, { client_name: client?.name || "", client_signature: sigs.client || "", technician_name: doc.technician || company?.responsible || "", technician_signature: sigs.tech || "" });
          final = r.data;
        }
        setSaved(final); localStorage.removeItem(draftKey); setStep(step + 1);
      } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); } finally { setSaving(false); }
      return;
    }
    if (steps[step + 1]?.id === "finish") {
      setSaving(true);
      try { const d = await persist(); setSaved(d); localStorage.removeItem(draftKey); setStep(step + 1); }
      catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); } finally { setSaving(false); }
      return;
    }
    setStep(step + 1);
  };

  const title = DOC_TYPES[type]?.label || "Documento";
  const locked = doc.status === "Assinado" && id;

  return (
    <div className="max-w-2xl mx-auto space-y-5 pb-28 md:pb-8" data-testid="document-wizard">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1 text-muted-foreground text-sm h-10" data-testid="wizard-exit"><X className="h-4 w-4" /> Sair</button>
        <p className="text-sm font-medium text-muted-foreground">{title} {saved?.number || doc.number || ""}</p>
        {id && cur !== "finish" && <Button size="sm" variant="ghost" onClick={() => navigate(`/documents/${id}/edit-classic`)} data-testid="wizard-classic"><Pencil className="h-3.5 w-3.5 mr-1" /> Completo</Button>}
        {!id && <span className="w-16" />}
      </div>

      {locked && <Card className="p-3 text-sm bg-amber-500/10 border-amber-500/30">Documento assinado — bloqueado para edição.</Card>}
      {cur !== "finish" && <WizardProgress steps={steps} current={step} onJump={setStep} />}

      <div className="sm:rounded-2xl sm:border sm:border-border sm:bg-card sm:p-6">
        <h2 className="font-display font-bold text-xl sm:text-2xl mb-5" data-testid="wizard-step-title">{TITLES[cur] || steps[step].label}</h2>
        {cur === "client" && <ClientStep clientId={doc.client_id} onSelect={(c) => { upd({ client_id: c.id }); setClient(c); setError(""); }} />}
        {cur === "equipment" && <EquipmentStep eq={doc.equipment} setEq={(equipment) => upd({ equipment, equipment_id: equipment.equipment_id ?? doc.equipment_id })} saved={equips.map((e) => ({ equipment_id: e.id, name: e.type === "Outro" ? e.name : e.type, brand: e.brand, model: e.model, serial: e.serial, btu: e.capacity, _label: e.label, _status: e.maintenance_status }))} selectedId={doc.equipment_id} />}
        {cur === "service" && <div className="space-y-6">
          {isService && <TextStep label="Descrição do problema (opcional)" value={doc.equipment.reported_defect} onChange={(v) => upd({ equipment: { ...doc.equipment, reported_defect: v } })} placeholder="Ex.: Não gela, faz barulho ao ligar." testid="wizard-defect" />}
          <TextStep label={type === "contrato" || type === "proposta_comercial" ? "Objeto / descrição do serviço" : "Descrição do serviço"} value={doc.description} onChange={(v) => upd({ description: v })} placeholder="Ex.: Troca do compressor, carga de gás e teste de funcionamento." testid="wizard-service"
            templates={company?.service_templates || []} onSaveTemplate={saveTemplate} onDeleteTemplate={deleteTemplate} />
          {isService && <AiDescribeButton kind="service" equipment={doc.equipment} seed={doc.description} onApply={applyAi} className="w-full" />}
          {isService && <PhotoUpload photos={doc.photos || []} onChange={(photos) => upd({ photos })} />}
          <ValueStep doc={doc} upd={upd} label="Valor do serviço" />
          <DateStep doc={doc} upd={upd} showValidity={type === "proposta_comercial"} />
        </div>}
        {cur === "defect" && <div className="space-y-6">
          <TextStep label="Descrição do problema" value={doc.equipment.reported_defect} onChange={(v) => upd({ equipment: { ...doc.equipment, reported_defect: v } })} placeholder="Ex.: Não gela, faz barulho ao ligar." testid="wizard-defect" />
          <AiDescribeButton kind={type === "report" ? "report" : "budget"} equipment={doc.equipment} seed={doc.equipment.reported_defect} onApply={applyAi} className="w-full" />
          {type === "budget" && <>
            <TextStep label="Descrição do serviço (opcional)" value={doc.description} onChange={(v) => upd({ description: v })} placeholder="Ex.: Substituição do compressor e carga de gás." testid="wizard-service" templates={company?.service_templates || []} onSaveTemplate={saveTemplate} onDeleteTemplate={deleteTemplate} />
            <ValueStep doc={doc} upd={upd} label="Valor do orçamento" />
            <DateStep doc={doc} upd={upd} showValidity />
          </>}
        </div>}
        {cur === "report" && <div className="space-y-6"><ReportStep doc={doc} upd={upd} /><DateStep doc={doc} upd={upd} /></div>}
        {cur === "photos" && <div className="space-y-2"><p className="text-sm text-muted-foreground">Opcional. As fotos saem no PDF.</p><PhotoUpload photos={doc.photos || []} onChange={(photos) => upd({ photos })} /></div>}
        {cur === "warranty" && <div className="space-y-6">
          <WarrantyStep warranty={doc.warranty} setWarranty={(warranty) => upd({ warranty })} />
          <TextStep label="Observações / condições (opcional)" value={doc.observations} onChange={(v) => upd({ observations: v })} placeholder="Informações adicionais para o cliente." testid="wizard-notes" />
        </div>}
        {cur === "notes" && <div className="space-y-6"><TextStep label="Observações (opcional)" value={doc.observations} onChange={(v) => upd({ observations: v })} placeholder="Informações adicionais para o cliente." testid="wizard-notes" /><DateStep doc={doc} upd={upd} /></div>}
        {cur === "review" && <ReviewStep doc={doc} type={type} client={client} steps={steps} onEdit={setStep} company={company} />}
        {cur === "sign" && <SignatureStep clientName={client?.name || "Cliente"} techName={doc.technician || company?.responsible} onTechName={(v) => upd({ technician: v })} sigs={sigs} setSigs={setSigs} />}
        {cur === "finish" && saved && <FinishStep doc={saved} client={client} type={type} />}
        {error && <p className="text-sm text-destructive mt-3" data-testid="wizard-error">{error}</p>}
      </div>

      {cur !== "finish" && (
        <div className="fixed bottom-20 left-0 right-0 md:static z-40 bg-background/95 backdrop-blur border-t md:border-0 border-border p-3 md:p-0">
          <div className="max-w-2xl mx-auto flex gap-2">
            <Button variant="outline" className="h-14 flex-1 text-base rounded-xl" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0 || saving} data-testid="wizard-back"><ArrowLeft className="h-5 w-5 mr-1" /> Voltar</Button>
            <Button className="h-14 flex-[2] text-base rounded-xl" onClick={next} disabled={saving || locked} data-testid="wizard-next">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : cur === "review" ? <>{steps[step + 1]?.id === "finish" ? "Gerar documento" : "Confirmar e assinar"} <Check className="h-5 w-5 ml-1" /></> : cur === "sign" ? <>Gerar documento <Check className="h-5 w-5 ml-1" /></> : <>Continuar <ArrowRight className="h-5 w-5 ml-1" /></>}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!draft} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" data-testid="wizard-draft-dialog">
          <DialogHeader><DialogTitle>Você tem um rascunho salvo</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Encontramos um {title.toLowerCase()} que você começou a preencher{draft?.doc?.labor ? ` (${fmtBRL(draft.doc.labor)})` : ""}. Deseja continuar de onde parou?</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { localStorage.removeItem(draftKey); setDraft(null); }} data-testid="wizard-draft-discard">Descartar</Button>
            <Button onClick={() => { setDoc({ ...emptyDoc(type), ...draft.doc }); setStep(draft.step || 0); if (draft.docId) setDocId(draft.docId); setDraft(null); }} data-testid="wizard-draft-continue">Continuar preenchimento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
